/**
 * Rooms API shapes. The live contract is `/api/rooms`, `/api/rooms/ops`,
 * nested messages, and mark-read. Payloads are normalized here so the UI
 * can tolerate `{ rooms }` vs a bare array and `authorType` vs `author_type`.
 */

export type RoomAuthorType = 'user' | 'agent' | 'system'

export interface AuthorMessage {
  id: string
  authorType: RoomAuthorType
  agentRole?: string
  authorName?: string
  content: string
  createdAt: string
}

export interface Room {
  id: string
  kind?: string
  name?: string
  agentRole?: string
  unreadCount: number
  lastActivityAt?: string
  lastMessage?: string
  messages?: AuthorMessage[]
}

export interface SeatConversation {
  id: string
  title: string
  agentRole?: string
  lastMessage: string
  updatedAt?: string
  messageCount: number
  unreadCount: number
  kind?: string
  isDefault?: boolean
}

export interface ActivityChip {
  unread: number
  lastActivityAt?: string
  preview?: string
}

export const OPS_READ_EVENT = 'forge:ops-read'

const HOME_KINDS = new Set(['home', 'default', 'agent_home', 'agent-home'])

function str(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function num(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value)
  return undefined
}

export function normalizeAuthorType(value: string | undefined, agentRole?: string): RoomAuthorType {
  const raw = (value ?? '').toLowerCase()
  if (raw === 'user' || raw === 'human' || raw === 'member') return 'user'
  if (raw === 'system' || raw === 'status' || raw === 'tick' || raw === 'wake') return 'system'
  if (raw === 'agent' || raw === 'assistant' || raw === 'bot') return 'agent'
  if (agentRole) return 'agent'
  return 'system'
}

export function normalizeAuthorMessage(raw: unknown): AuthorMessage | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const agentRole = str(record.agentRole) ?? str(record.agent_role) ?? str(record.agent)
  const authorType = normalizeAuthorType(
    str(record.authorType) ?? str(record.author_type) ?? str(record.role) ?? str(record.author),
    agentRole,
  )
  const content = str(record.content) ?? str(record.body) ?? str(record.text) ?? str(record.message) ?? str(record.status) ?? str(record.summary) ?? ''
  if (!content) return null
  const createdAt = str(record.createdAt) ?? str(record.created_at) ?? str(record.timestamp) ?? str(record.at) ?? new Date(0).toISOString()
  const id = str(record.id) ?? str(record.messageId) ?? str(record.message_id) ?? `msg-${createdAt}-${content.slice(0, 24)}`
  return {
    id,
    authorType,
    agentRole,
    authorName: str(record.authorName) ?? str(record.author_name),
    content,
    createdAt,
  }
}

export function unwrapRecord(raw: unknown, keys: string[]): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const record = raw as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>
  }
  return record
}

function arrayFrom(raw: unknown, keys: string[]): unknown[] {
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  const record = raw as Record<string, unknown>
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as unknown[]
  }
  return []
}

export function normalizeRoom(raw: unknown): Room | null {
  const record = unwrapRecord(raw, ['room', 'ops'])
  if (!record) return null
  const kind = str(record.kind) ?? str(record.type)
  const id = str(record.id) ?? str(record.roomId) ?? str(record.room_id) ?? str(record.slug) ?? (kind === 'ops' ? 'ops' : undefined)
  if (!id) return null
  const messagesRaw = record.messages
  const messages = Array.isArray(messagesRaw)
    ? messagesRaw.map(normalizeAuthorMessage).filter((message): message is AuthorMessage => message !== null)
    : undefined
  return {
    id,
    kind,
    name: str(record.name) ?? str(record.title),
    agentRole: str(record.agentRole) ?? str(record.agent_role),
    unreadCount: num(record.unreadCount) ?? num(record.unread_count) ?? num(record.unread) ?? num(record.unreadMessages) ?? 0,
    lastActivityAt: str(record.lastActivityAt) ?? str(record.last_activity_at) ?? str(record.updatedAt) ?? str(record.updated_at),
    lastMessage: str(record.lastMessage) ?? str(record.last_message) ?? str(record.preview),
    messages,
  }
}

export function normalizeRoomList(raw: unknown): Room[] {
  return arrayFrom(raw, ['rooms', 'items', 'data'])
    .map(normalizeRoom)
    .filter((room): room is Room => room !== null)
}

export function normalizeRoomMessages(raw: unknown): AuthorMessage[] {
  return arrayFrom(raw, ['messages', 'items', 'data'])
    .map(normalizeAuthorMessage)
    .filter((message): message is AuthorMessage => message !== null)
}

const OPS_LABEL = /^(ops|ops room|ops-room)$/i

function looksLikeOps(kind: string | undefined, id: string | undefined, label: string | undefined): boolean {
  const normalizedKind = (kind ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-')
  if (normalizedKind === 'ops' || normalizedKind === 'ops-room') return true
  const normalizedId = (id ?? '').trim().toLowerCase()
  if (normalizedId === 'ops' || normalizedId === 'ops-room') return true
  return OPS_LABEL.test((label ?? '').trim())
}

/**
 * The org ops room, including when the payload also carries an agent role
 * (the operator seat was inheriting that unread).
 */
export function isOpsRoom(room: Room): boolean {
  return looksLikeOps(room.kind, room.id, room.name)
}

function isOpsConversation(conversation: SeatConversation, opsIds: Set<string>): boolean {
  if (opsIds.has(conversation.id)) return true
  return looksLikeOps(conversation.kind, conversation.id, conversation.title)
}

/**
 * Unread belongs on a seat only when that seat's own thread holds it.
 * An empty thread has nothing unread. Operator also drops a count that is
 * just the ops room stamped onto the 1:1.
 */
function conversationOwnsUnread(conversation: SeatConversation, ops: ActivityChip): boolean {
  if (conversation.unreadCount <= 0) return false
  const preview = conversation.lastMessage.trim()
  if (conversation.messageCount <= 0 && !preview) return false
  if (conversation.agentRole !== 'operator') return true
  if (conversation.messageCount <= 0) return false
  if (
    ops.unread > 0 &&
    conversation.unreadCount === ops.unread &&
    !!ops.preview &&
    preview === ops.preview
  ) {
    return false
  }
  return true
}

function roomUnreadForSeat(room: Room, ops: ActivityChip): number {
  if (room.unreadCount <= 0) return 0
  if (room.agentRole !== 'operator') return room.unreadCount
  if (ops.unread <= 0 || room.unreadCount !== ops.unread) return room.unreadCount
  if (!room.lastMessage || room.lastMessage === ops.preview) return 0
  return room.unreadCount
}

export function mergeOpsRoom(rooms: Room[], ops: Room | null): Room[] {
  const rest = rooms.filter((room) => !isOpsRoom(room))
  return ops ? [...rest, ops] : rest
}

function bump(chip: ActivityChip, at?: string, preview?: string) {
  if (!at) {
    if (!chip.preview && preview) chip.preview = preview
    return
  }
  const next = Date.parse(at)
  const prev = chip.lastActivityAt ? Date.parse(chip.lastActivityAt) : Number.NaN
  if (!chip.lastActivityAt || (Number.isFinite(next) && (!Number.isFinite(prev) || next >= prev))) {
    chip.lastActivityAt = at
    if (preview) chip.preview = preview
  }
}

export function seatActivity(conversations: SeatConversation[], rooms: Room[]): Record<string, ActivityChip> {
  const out: Record<string, ActivityChip> = {}
  const roomBacked = new Set<string>()
  const ops = opsActivity(rooms)
  const opsIds = new Set(rooms.filter(isOpsRoom).map((room) => room.id))

  for (const room of rooms) {
    if (isOpsRoom(room) || !room.agentRole) continue
    if (!out[room.agentRole]) out[room.agentRole] = { unread: 0 }
    roomBacked.add(room.agentRole)
    const chip = out[room.agentRole]
    chip.unread += roomUnreadForSeat(room, ops)
    bump(chip, room.lastActivityAt, room.lastMessage)
  }

  for (const conversation of conversations) {
    if (!conversation.agentRole || isOpsConversation(conversation, opsIds)) continue
    if (!out[conversation.agentRole]) out[conversation.agentRole] = { unread: 0 }
    const chip = out[conversation.agentRole]
    if (!roomBacked.has(conversation.agentRole) && conversationOwnsUnread(conversation, ops)) {
      chip.unread += conversation.unreadCount
    }
    bump(chip, conversation.updatedAt, conversation.lastMessage)
  }

  return out
}

export function opsActivity(rooms: Room[], conversations: SeatConversation[] = []): ActivityChip {
  const chip: ActivityChip = { unread: 0 }
  const opsIds = new Set<string>()
  for (const room of rooms) {
    if (!isOpsRoom(room)) continue
    opsIds.add(room.id)
    chip.unread += room.unreadCount
    bump(chip, room.lastActivityAt, room.lastMessage)
  }

  for (const conversation of conversations) {
    if (!isOpsConversation(conversation, opsIds) || opsIds.has(conversation.id)) continue
    chip.unread += conversation.unreadCount
    bump(chip, conversation.updatedAt, conversation.lastMessage)
  }

  // The operator 1:1 was showing the ops-room count while its own thread
  // was empty. If the room payload didn't carry the count, keep it on Ops.
  if (chip.unread === 0) {
    for (const conversation of conversations) {
      if (conversation.agentRole !== 'operator' || conversation.messageCount > 0) continue
      if (conversation.unreadCount <= 0 || isOpsConversation(conversation, opsIds)) continue
      chip.unread += conversation.unreadCount
      bump(chip, conversation.updatedAt, conversation.lastMessage)
    }
  }

  return chip
}

export function normalizeConversation(raw: unknown): SeatConversation | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const id = str(record.id)
  if (!id) return null
  return {
    id,
    title: str(record.title) ?? 'Conversation',
    agentRole: str(record.agentRole) ?? str(record.agent_role),
    lastMessage: str(record.lastMessage) ?? str(record.last_message) ?? '',
    updatedAt: str(record.updatedAt) ?? str(record.updated_at),
    messageCount: num(record.messageCount) ?? num(record.message_count) ?? 0,
    unreadCount: num(record.unreadCount) ?? num(record.unread_count) ?? num(record.unread) ?? 0,
    kind: str(record.kind),
    isDefault: record.isDefault === true || record.is_default === true,
  }
}

export function asConversations(raw: unknown): SeatConversation[] {
  return arrayFrom(raw, ['conversations', 'items', 'data'])
    .map(normalizeConversation)
    .filter((conversation): conversation is SeatConversation => conversation !== null)
}

export function pickHomeConversation(conversations: SeatConversation[], role: string): SeatConversation | null {
  const mine = conversations.filter((conversation) => conversation.agentRole === role)
  const flagged = mine.find((conversation) => conversation.isDefault || (conversation.kind ? HOME_KINDS.has(conversation.kind) : false))
  if (flagged) return flagged
  return [...mine].sort((a, b) => {
    const aTime = a.updatedAt ? Date.parse(a.updatedAt) : 0
    const bTime = b.updatedAt ? Date.parse(b.updatedAt) : 0
    return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0)
  })[0] ?? null
}

/** Map a chat-thread message (`role` + `timestamp`) onto a room author bubble. */
export function fromChatMessage(
  message: { id: string; role?: string; content?: string; timestamp?: string; agentRole?: string },
  fallbackRole: string,
): AuthorMessage | null {
  const content = message.content?.trim() ?? ''
  if (!content) return null
  const role = (message.role ?? '').toLowerCase()
  const authorType: RoomAuthorType = role === 'user' ? 'user' : role === 'system' ? 'system' : 'agent'
  return {
    id: message.id,
    authorType,
    agentRole: message.agentRole ?? (authorType === 'agent' ? fallbackRole : undefined),
    content,
    createdAt: message.timestamp ?? new Date(0).toISOString(),
  }
}
